import React, { useRef, useState } from "react";
import { X, Download, RotateCw } from "lucide-react";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { toast } from "sonner";
import { KartuPelajarFront, KartuPelajarBack } from "./KartuPelajar";

export default function KartuPelajarModal({ siswa, onClose }) {
  const [side, setSide] = useState("depan"); // depan | belakang
  const [busy, setBusy] = useState(false);
  const frontRef = useRef(null);
  const backRef = useRef(null);

  const download = async () => {
    if (!frontRef.current || !backRef.current) return;
    setBusy(true);
    try {
      const opts = { scale: 3, useCORS: true, backgroundColor: "#ffffff", logging: false };
      const [frontCanvas, backCanvas] = await Promise.all([
        html2canvas(frontRef.current, opts),
        html2canvas(backRef.current, opts),
      ]);
      const frontImg = frontCanvas.toDataURL("image/png");
      const backImg = backCanvas.toDataURL("image/png");
      // A4 portrait, mm
      const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
      const pageW = 210, pageH = 297;
      const cardW = 150;
      const cardH = (cardW * 340) / 540; // maintain aspect (540x340)
      const x = (pageW - cardW) / 2;
      const gap = 12;
      const totalH = cardH * 2 + gap;
      const yStart = (pageH - totalH) / 2;
      // Title
      pdf.setFontSize(14);
      pdf.setTextColor(30, 58, 95);
      pdf.text(`Kartu Pelajar — ${siswa?.nama || ""}`, pageW / 2, 18, { align: "center" });
      pdf.addImage(frontImg, "PNG", x, yStart, cardW, cardH);
      pdf.setFontSize(9);
      pdf.setTextColor(100, 100, 100);
      pdf.text("Bagian Depan", pageW / 2, yStart + cardH + 5, { align: "center" });
      pdf.addImage(backImg, "PNG", x, yStart + cardH + gap, cardW, cardH);
      pdf.text("Bagian Belakang", pageW / 2, yStart + cardH + gap + cardH + 5, { align: "center" });
      const fname = `kartu-pelajar-${(siswa?.nama || "siswa").toLowerCase().replace(/\s+/g, "-")}.pdf`;
      pdf.save(fname);
      toast.success("Kartu Pelajar berhasil didownload");
    } catch (e) {
      console.error(e);
      toast.error("Gagal generate PDF: " + (e.message || "unknown"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="kartu-pelajar-modal">
      <div className="bg-white rounded-2xl max-w-2xl w-full p-6 my-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-lg font-bold text-slate-900">Kartu Pelajar</h3>
          <button onClick={onClose} aria-label="Tutup"><X className="w-5 h-5 text-slate-400 hover:text-slate-700" /></button>
        </div>

        {/* Card display */}
        <div className="flex flex-col items-center mb-5 bg-slate-50 rounded-xl p-6">
          <div style={{ display: side === "depan" ? "block" : "none" }}>
            <KartuPelajarFront ref={frontRef} siswa={siswa} />
          </div>
          <div style={{ display: side === "belakang" ? "block" : "none" }}>
            <KartuPelajarBack ref={backRef} />
          </div>
          {/* Always render both off-screen for capture (in case user hasn't toggled) */}
          {side === "depan" && <div style={{ position: "absolute", left: -10000, top: -10000 }}><KartuPelajarBack ref={backRef} /></div>}
          {side === "belakang" && <div style={{ position: "absolute", left: -10000, top: -10000 }}><KartuPelajarFront ref={frontRef} siswa={siswa} /></div>}

          <div className="text-xs text-slate-500 mt-3">Bagian {side === "depan" ? "Depan" : "Belakang"}</div>
        </div>

        {/* Actions */}
        <div className="flex flex-wrap justify-end gap-2">
          <button
            type="button"
            onClick={() => setSide(side === "depan" ? "belakang" : "depan")}
            className="pp-btn-secondary"
            data-testid="kartu-pelajar-flip"
          >
            <RotateCw className="w-4 h-4" /> Lihat {side === "depan" ? "Belakang" : "Depan"}
          </button>
          <button
            type="button"
            onClick={download}
            disabled={busy}
            className="pp-btn-primary"
            data-testid="kartu-pelajar-download"
          >
            <Download className="w-4 h-4" /> {busy ? "Memproses…" : "Download PDF"}
          </button>
        </div>
      </div>
    </div>
  );
}
